<script lang="ts" setup>
import { segmentation, segmentationV1 } from '@thecompaniesapi/shared/client'

const { getCompanyAttributes, getMetaAttributes, getSortAttributes } = useApiAttributes()
const { refreshResponseWithApiVersion, version } = useApiDoc()
const { t } = useI18n()
const api = useApi()
const localePath = useLocalePath()

const { data: response, execute, status } = useAsyncData(
  'doc:companies-search-similar',
  () => {
    const companyDomain = version.value === 'v1'
      ? segmentationV1.freeQueries.domain
      : segmentation.freeQueries.domain

    return api.companies.fetchSimilar({
      domains: [companyDomain],
      page: 1,
      size: 10,
    })
  },
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: `/companies/similar?domains[]=${segmentation.freeQueries.domain}&page=1&size=10`,
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.domains'),
    example: `…/companies/similar?domains[]=${segmentation.freeQueries.domain}&domains[]=wix.com`,
    name: 'domains',
    required: true,
    type: 'array',
  },
  {
    description: t('attributes.page'),
    example: '/companies/similar?domains[]=…&page=1',
    name: 'page',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.simplified'),
    example: '/companies/similar?domains[]=…&simplified=true',
    name: 'simplified',
    optional: true,
    type: 'boolean',
    values: ['false', 'true'],
  },
  {
    description: t('attributes.size'),
    example: '/companies/similar?domains[]=…&size=50',
    name: 'size',
    optional: true,
    type: 'number',
  },
  ...getSortAttributes({ endpoint: '/companies/similar' }),
]

const responseAttributes = computed((): ApiAttribute[] => {
  return [
    {
      attributes: getCompanyAttributes({
        parent: 'similarCompany',
        specialFields: [
          'similarAttributes',
          'similarDomains',
          'similitude',
        ],
        version: version.value,
      }),
      description: t('response.companies'),
      link: {
        label: t('globals.browseAllDatapoints'),
        to: localePath({
          name: 'api-slug',
          params: { slug: 'datapoints' },
        }),
      },
      name: 'companies',
      type: 'array',
    },
    getMetaAttributes(),
  ]
})

async function onVisibilityChange(visibility: boolean) {
  refreshResponseWithApiVersion({
    api,
    execute,
    response,
    visibility,
  })
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "domains": "A list of domains separated by commas.",
      "page": "The page to fetch (default to 1).",
      "simplified": "Return a simplified version of the companies (no credits deducted but limitation are applied on the free plan). It is useful for previewing the data before fetching the full version. Default to false.",
      "size": "The number of similar companies to be returned (between 1 and 100). Default to 10."
    },
    "response": {
      "companies": "The similar companies for your list of domains."
    }
  },
  "es": {
    "attributes": {
      "domains": "Una lista de nombres de dominio separados por comas.",
      "page": "La página a recuperar (por defecto 1).",
      "simplified": "Return a simplified version of the companies (no credits deducted but limitation are applied on the free plan). It is useful for previewing the data before fetching the full version. Default to false.",
      "size": "El número de empresas similares a devolver (entre 1 y 100). Por defecto a 10."
    },
    "response": {
      "companies": "Las empresas similares para tu lista de nombres de dominio."
    }
  },
  "fr": {
    "attributes": {
      "domains": "Une liste de domaines séparés par des virgules.",
      "page": "La page à récupérer (par défaut à 1).",
      "simplified": "Retourne une version simplifiée des entreprises (sans déduction de crédits mais des limitations sont appliquées sur le plan gratuit). Il est utile pour visualiser les données avant de récupérer la version complète. Par défaut à false.",
      "size": "Le nombre de sociétés similaires à retourner (entre 1 et 100). Par défaut à 10."
    },
    "response": {
      "companies": "Les sociétés similaires pour votre liste de domaines."
    }
  }
}
</i18n>
